import React, { useState } from "react";
import { Spinner } from "react-bootstrap";
import { Pencil, Trash2, StickyNote, UserPlus, Eye, Phone, CalendarClock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AppModal, AppButton, ConfirmModal, ActionMenuItem, ActionMenuDivider } from "../ui";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import useClickToCall from "../../hooks/useClickToCall";
import CallStatusToast from "../Telephony/CallStatusToast";
import FollowUpModal from "./FollowUpModal";

/**
 * Shared enquiry row-action logic.
 *
 * Returns a `renderItems(enquiry)` factory that produces the menu items and a
 * single `modals` node (add-note + convert-to-student) that the caller renders
 * once. This lets the kebab dropdown and the right-click context menu drive the
 * exact same set of actions without duplicating state or dialogs per row.
 *
 * Delete is delegated to the caller via `onDelete` so the existing list-level
 * delete confirmation is reused.
 *
 * `canDelete` comes from the ownership policy (see `useEnquiryOwnership`).
 * Under Enquiry Ownership Control only the institute Super Admin may delete, so
 * the item is dropped rather than shown-and-refused.
 */
export default function useEnquiryActions({ onDelete, onNoteAdded, onConverted, canDelete = true, canEdit = true, canConvert = true }) {
  const navigate = useNavigate();
  // A finished call writes itself onto the enquiry timeline server-side, so the
  // list needs the same refresh a new note triggers.
  const call = useClickToCall({ onLogged: () => onNoteAdded?.() });
  const [followUpTarget, setFollowUpTarget] = useState(null);
  const [noteTarget, setNoteTarget] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [convertTarget, setConvertTarget] = useState(null);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState("");

  const closeNote = () => {
    setNoteTarget(null);
    setNoteText("");
  };

  const handleSaveNote = async () => {
    if (!noteText.trim() || !noteTarget) return;
    setSavingNote(true);
    try {
      const user = getCurrentUser();
      await apiClient.post(`/enquiries/${noteTarget._id}/notes`, {
        note: noteText.trim(),
        createdBy: user?.name || "Admin",
      });
      closeNote();
      onNoteAdded?.();
    } catch {
      /* silent — note save is non-critical */
    }
    setSavingNote(false);
  };

  const handleConvert = async () => {
    if (!convertTarget) return;
    setConverting(true);
    setError("");
    try {
      await apiClient.post(`/enquiries/${convertTarget._id}/convert-to-student`);
      setConvertTarget(null);
      onConverted?.();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to convert enquiry to student.");
    }
    setConverting(false);
  };

  const renderItems = (enquiry) => (
    <>
      {/* Only offered when the institute has calling configured. Without it the
          contact column's tel: link is still there, so nothing is lost. */}
      {call.enabled && enquiry.mobile && (
        <ActionMenuItem
          disabled={call.calling}
          onClick={() => call.placeCall({ enquiryId: enquiry._id })}
        >
          <Phone size={14} /> Call
        </ActionMenuItem>
      )}
      {canEdit && (
        <ActionMenuItem onClick={() => setFollowUpTarget(enquiry)}>
          <CalendarClock size={14} /> {enquiry.nextFollowUpAt ? "Reschedule follow-up" : "Schedule follow-up"}
        </ActionMenuItem>
      )}
      {canConvert && enquiry.status !== "Converted" && (
        <ActionMenuItem onClick={() => setConvertTarget(enquiry)}>
          <UserPlus size={14} /> Move to Student
        </ActionMenuItem>
      )}
      <ActionMenuItem onClick={() => navigate(`/enquiries/${enquiry._id}`)}>
        <Eye size={14} /> View
      </ActionMenuItem>
      {canEdit && (
        <>
          <ActionMenuItem onClick={() => { setNoteTarget(enquiry); setNoteText(""); }}>
            <StickyNote size={14} /> Add Note
          </ActionMenuItem>
          <ActionMenuItem onClick={() => navigate(`/enquiries/edit/${enquiry._id}`)}>
            <Pencil size={14} /> Edit
          </ActionMenuItem>
        </>
      )}
      {canDelete && (
        <>
          <ActionMenuDivider />
          <ActionMenuItem danger onClick={() => onDelete?.(enquiry._id)}>
            <Trash2 size={14} /> Delete
          </ActionMenuItem>
        </>
      )}
    </>
  );

  const modals = (
    <>
      <CallStatusToast call={call.activeCall} error={call.error} onDismiss={call.dismiss} />

      <FollowUpModal
        show={!!followUpTarget}
        onHide={() => setFollowUpTarget(null)}
        enquiry={followUpTarget}
        onSaved={() => onNoteAdded?.()}
      />

      <AppModal
        show={!!noteTarget}
        onHide={closeNote}
        title="Add Follow-up Note"
        icon={<StickyNote size={18} />}
        size="sm"
        footer={
          <>
            <AppButton variant="ghost" size="sm" onClick={closeNote} disabled={savingNote}>
              Cancel
            </AppButton>
            <AppButton size="sm" onClick={handleSaveNote} disabled={!noteText.trim() || savingNote}>
              {savingNote ? <Spinner animation="border" size="sm" /> : "Save note"}
            </AppButton>
          </>
        }
      >
        {noteTarget && (
          <p className="eq-note-modal__for">
            For <strong>{noteTarget.fullName || "this enquiry"}</strong>
          </p>
        )}
        <textarea
          rows={4}
          className="eq-note-modal__input"
          placeholder="Write a note…"
          value={noteText}
          autoFocus
          onChange={(e) => setNoteText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSaveNote();
            }
          }}
        />
      </AppModal>

      <ConfirmModal
        show={!!convertTarget}
        onHide={() => { setConvertTarget(null); setError(""); }}
        onConfirm={handleConvert}
        title="Move to Student"
        message={
          <>
            Move <strong>{convertTarget?.fullName}</strong> to Students? This will create a new
            student record and mark this enquiry as Converted.
            {error && <div className="text-danger mt-2" style={{ fontSize: "0.85rem" }}>{error}</div>}
          </>
        }
        confirmText="Move to Student"
        cancelText="Cancel"
        variant="success"
        loading={converting}
      />
    </>
  );

  return {
    renderItems,
    modals,
    // Exposed so a list row can offer a one-click dial without duplicating the
    // hook (and its in-flight guard) per row.
    canCall: call.enabled,
    callEnquiry: (enquiry) => call.placeCall({ enquiryId: enquiry._id }),
  };
}
